import React, { useState, useEffect } from "react";
import * as Yup from "yup";

import Form from "@Components/Form";
import eightAxios from "configs/axiosConfig";
import { BANNER_URL } from "configs/constants";
import LoadingIcon from "@Components/LoadingIcon/LoadingIcon";
import { TrafficOutlined } from "@material-ui/icons";

const initialValues = {
  banner_type: "",
  title: "",
  description: "",
  call_to_action: false,
  url: "",
  button_yes: "",
  button_no:  "",
  image: null,
  is_active: false,
  display_start_date: null,
  display_start_time: null,
};

const SUPPORTED_IMAGE_FORMATS = [
  "image/jpg",
  "image/jpeg",
  "image/gif",
  "image/png",
];

const detailURL = "/banner/details/";

const BannerForm = () => {
  const [bannerTypes, setBannerTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [selectedButtons, setSelectedButtons] = useState(true)


  const validationSchema = Yup.object({
    banner_type: Yup.string().nullable().required("Banner type is required"),
  title: Yup.string().nullable().required("Banner title is required"),
  display_start_date: Yup.string()
    .nullable()
    .required("Display start date is required"),
  image: Yup.mixed()
    .required("Banner image is required")
    .test("fileType", "Unsupported file type", (value) => {
      if (value === null) {
        return false;
      } else if (typeof value === "string") {
        return true;
      } else {
        return SUPPORTED_IMAGE_FORMATS.includes(value.type);
      }
    }),
  url: Yup.string()
    .matches(
      /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/,
      "Url must start with https://"
    ).when("call_to_action", {
      is: true,
      then: Yup.string()
      .when("banner_type", {
        is: "IAB", // Replace "certainValue" with the value that triggers this condition
        then: Yup.string().required("URL is required and should start with https:"),
        otherwise: Yup.string().nullable(),
      }),
      otherwise: Yup.string().nullable(),
    }),
  call_to_action: Yup.boolean(),
  button_yes: Yup.string().when("call_to_action", {
    is: true,
    then: Yup.string()
    .when("banner_type", {
      is: "PB", 
      then: Yup.string().nullable().required("Button Yes is required"),
      otherwise: Yup.string().nullable(),
    }),
    otherwise: Yup.string().nullable(),
  }),
  button_no: Yup.string().when("call_to_action", {
    is: true,
    then: Yup.string()
    .when("banner_type", {
      is: "PB", 
      then: Yup.string().nullable().required("Button No is required"),
      otherwise: Yup.string().nullable(),
    }),
    otherwise: Yup.string().nullable(),
  }),
  });
  
  validationSchema.test(
    "call_to_action-required",
    "Either Button Yes or Button No is required, but not both",
    function () {
      const { call_to_action, button_yes, button_no } = this.parent;
      return (call_to_action && (button_yes || button_no)) || !call_to_action;
    }
  );

  const mapBannerTypes = (types) => {
    let keys = Object.keys(types);
    let tempTypes = [];
    let cnt = 0;
    keys.forEach((key) => {
      tempTypes[cnt] = { id: key, name: types[key] };
      cnt++;
    });

    setBannerTypes(tempTypes);
  };

  const fetchDetails = () => {
    setLoading(true);
    eightAxios
      .get(detailURL + "?from_frontend=true")
      .then((response) => {
        if (response.status === 200) {
          if (response.data.types) {
            mapBannerTypes(response.data.types);

          }
        }
        setLoading(false);
      })
      .catch((error) => {
        if (error.response) {
          console.log(error.response);
        }
        setLoading(false);
      });
  };
 
  const getAllButtons = (e) => {
    setSelectedButtons(e?.value);
  };

  useEffect(() => {
    fetchDetails();
  }, []);
  useEffect(() => {

    if (selectedButtons == true) {
      setShowButtons(true);
    } else {
      setShowButtons(false);
    }


  }, [selectedButtons]);
  const formFields = [
    {
      name: "is_active",
      type: "switch",
      label: "Is Active?",
      color: "primary",
      gridSize: { xs: 12 },
    },
    {
      type: "nosearchselect",
      name: "banner_type",
      size: "small",
      label: "Banner Type",
      required: true,
      options: bannerTypes,
      // disabled: true,
      gridSize: { xs: 12, sm: 4, md: 3, lg: 3 },
    },
    {
      type: "input",
      variant: "outlined",
      autoComplete: "off",
      name: "title",
      size: "small",
      label: "Banner Title",
      required: true,
      gridSize: { xs: 12, sm: 8, md: 9, lg: 9 },
    },
    {
      type: "fileupload",
      name: "image",
      supportedTypes: ["Example: 1200 x 627 px"],
      accept: "image/*",
      label: "Image (jpg, jpeg, png)",
      required: true,
      buttonText: "Select Image",
      gridSize: { lg: 4 },
    },
    {
      type: "datepicker",
      name: "display_start_date",
      autoComplete: "off",
      size: "small",
      helperText: "",
      label: "Display Start Date",
      required: true,
      gridSize: { xs: 12, sm: 8, md: 9, lg: 4 },
    },
    {
      type: "timepicker",
      name: "display_start_time",
      autoComplete: "off",
      size: "small",
      helperText: "",
      label: "Display Start Time",
      gridSize: { xs: 12, sm: 8, md: 9, lg: 4 },
      emptyLabel: "",
    },
    {
      type: "input",
      variant: "outlined",
      name: "description",
      fullWidth: true,
      multiline: true,
      rows: 3,
      size: "medium",
      label: "Description",
      gridSize: { sm: 12 },
    },
    {
      name: "call_to_action",
      type: "switch",
      label: "Call to action?",
      color: "primary",
      gridSize: { xs: 12 },
      getValue: (e) => getAllButtons(e),

    },
    {
      type: "input",
      variant: "outlined",
      name: "url",
      fullWidth: true,
      requiredValidation: "call_to_action",
      multiline: true,
      rows: 3,
      size: "medium",
      renderKey: "banner_type",
      renderValue: "IAB",
      label: "URL",
      gridSize: { sm: 12 },
      showField: showButtons,
    },
    {
      type: "ButtonInput",
      variant: "outlined",
      name: "button_yes",
      fullWidth: true,
      multiline: true,
      rows: 3,
      size: "medium",
      label: "Button Yes",
      required: true,
      renderKey: "banner_type",
      renderValue: "PB",
      gridSize: { sm: 12 },
      maxCharacterLimit: 100,
      showField: showButtons,

    },
    {
      type: "ButtonInput",
      variant: "outlined",
      name: "button_no",
      required: true,
      key: "button_no",
      fullWidth: true,
      multiline: true,
      rows: 3,
      size: "medium",
      label: "Button No",
      renderKey: "banner_type",
      renderValue: "PB",
      gridSize: { sm: 12 },
      maxCharacterLimit: 100,
      showField: showButtons,




    },
  ];

  const formButtons = ["submit", "cancel"];

  if (loading) {
    return <LoadingIcon image text="Loading" />;
  }
  

  return (
    <Form
      initialValues={initialValues}
      submitUrl={BANNER_URL}
      listRoute="/announcement/banner/banner"
      formFields={formFields}
      formButtons={formButtons}
      formTitle="Banner"
      isMultipart
      noListButton
      validationSchema={validationSchema}
    />
  );
};

export default BannerForm;


